import * as RadixSelect from '@radix-ui/react-select';
import * as Label from '@radix-ui/react-label';
import { Check, ChevronDown } from 'lucide-react';
import { useId } from 'react';
import { cn } from '@/lib/cn';

/*
  Single-select built on Radix, not a native <select> and not a div listbox.

  Radix gives typeahead, home and end, arrow navigation, escape to close, focus return to
  the trigger, and the aria-activedescendant bookkeeping that a hand-rolled dropdown always
  gets wrong. A native <select> would also be accessible but cannot be styled consistently
  across Windows browsers, and this console is used on Windows by definition.
*/

export interface SelectOption {
  readonly value: string;
  readonly label: string;
}

export interface SelectProps {
  readonly label: string;
  readonly value: string;
  readonly options: readonly SelectOption[];
  readonly onValueChange: (value: string) => void;
  readonly labelHidden?: boolean;
  readonly className?: string;
  readonly triggerClassName?: string;
}

export function Select({
  label,
  value,
  options,
  onValueChange,
  labelHidden = false,
  className,
  triggerClassName,
}: SelectProps): React.JSX.Element {
  const uid = useId();
  const triggerId = `select-${uid}`;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label.Root
        htmlFor={triggerId}
        className={cn(
          'text-xs font-semibold tracking-wide text-fg-muted uppercase',
          labelHidden && 'sr-only',
        )}
      >
        {label}
      </Label.Root>
      <RadixSelect.Root value={value} onValueChange={onValueChange}>
        <RadixSelect.Trigger
          id={triggerId}
          className={cn(
            'inline-flex h-9 items-center justify-between gap-2 rounded-[var(--radius-control)]',
            'border border-border bg-surface px-3 text-sm text-fg',
            'hover:border-border-strong data-[placeholder]:text-fg-subtle',
            triggerClassName,
          )}
        >
          <RadixSelect.Value />
          <RadixSelect.Icon>
            <ChevronDown className="size-4 text-fg-subtle" aria-hidden="true" />
          </RadixSelect.Icon>
        </RadixSelect.Trigger>
        <RadixSelect.Portal>
          <RadixSelect.Content
            position="popper"
            sideOffset={4}
            className="animate-pop-in z-50 max-h-72 min-w-[10rem] overflow-hidden rounded-[var(--radius-control)] border border-border bg-surface-raised shadow-lg"
          >
            <RadixSelect.Viewport className="p-1">
              {options.map((option) => (
                <RadixSelect.Item
                  key={option.value}
                  value={option.value}
                  className={cn(
                    'relative flex cursor-default items-center gap-2 rounded px-2 py-1.5 pl-7 text-sm text-fg select-none',
                    'data-highlighted:bg-accent-soft data-highlighted:text-accent-soft-fg data-highlighted:outline-none',
                  )}
                >
                  <RadixSelect.ItemIndicator className="absolute left-2">
                    <Check className="size-3.5" aria-hidden="true" />
                  </RadixSelect.ItemIndicator>
                  <RadixSelect.ItemText>{option.label}</RadixSelect.ItemText>
                </RadixSelect.Item>
              ))}
            </RadixSelect.Viewport>
          </RadixSelect.Content>
        </RadixSelect.Portal>
      </RadixSelect.Root>
    </div>
  );
}
